import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform, useReducedMotion } from "framer-motion";
import { ArrowRight, GraduationCap, Users, Compass, Award, Sparkles, MapPin, BookOpen, Calendar } from "lucide-react";
import { fetchPrograms } from "../lib/api";
import { Reveal, Stagger, staggerItem, Magnetic } from "../components/Motion";

const HERO_IMG =
  "https://images.pexels.com/photos/7683745/pexels-photo-7683745.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const PROGRAM_IMG =
  "https://images.unsplash.com/photo-1557734864-c78b6dfef1b1?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTN8MHwxfHNlYXJjaHwxfHxidXNpbmVzcyUyMG1hbmFnZW1lbnQlMjBzdHVkZW50c3xlbnwwfHx8fDE3NzgwMTQyNjl8MA&ixlib=rb-4.1.0&q=85";

const facts = [
  { icon: Sparkles, label: "Founded", value: "2025" },
  { icon: MapPin, label: "Located in", value: "Ontario, Canada" },
  { icon: BookOpen, label: "Now enrolling", value: "Business Management Diploma" },
  { icon: Calendar, label: "First intake", value: "2026" },
];

const pillars = [
  { icon: GraduationCap, title: "Career-first curriculum", body: "Programs designed with industry advisors so graduates can step into work-ready roles from day one." },
  { icon: Users, title: "Mentorship-led classrooms", body: "Small cohorts and accessible educators — every learner is mentored, not just taught." },
  { icon: Compass, title: "Pathways across Canada", body: "Heritage diplomas are designed to open doors to employers and pathways to undergraduate study." },
  { icon: Award, title: "Held to a higher standard", body: "Built by educators who care about graduate outcomes, integrity, and lifelong support." },
];

const Home = () => {
  const [programs, setPrograms] = useState([]);
  const reduce = useReducedMotion();

  // Hero parallax
  const { scrollY } = useScroll();
  const heroY = useTransform(scrollY, [0, 600], reduce ? [0, 0] : [0, 80]);
  const heroScale = useTransform(scrollY, [0, 400], reduce ? [1, 1] : [1, 1.06]);
  const blobY = useTransform(scrollY, [0, 600], reduce ? [0, 0] : [0, -60]);

  useEffect(() => {
    fetchPrograms().then(setPrograms).catch(() => {});
  }, []);

  const activeProgram = programs.find((p) => p.status === "active");
  const upcoming = programs.filter((p) => p.status === "coming_soon").slice(0, 3);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative overflow-hidden">
        {/* Animated background blobs */}
        <motion.div
          style={{ y: blobY }}
          className="blob"
          aria-hidden
        >
          <div className="blob" style={{ width: 380, height: 380, background: "#87CEEB", top: -80, right: -120, position: "absolute" }} />
        </motion.div>
        <div className="blob" style={{ width: 280, height: 280, background: "#F59E0B", opacity: 0.18, bottom: 40, left: -80, animationDelay: "-4s" }} aria-hidden />
        <div className="absolute inset-0 -z-10 subtle-grid-bg opacity-30" aria-hidden />

        <div className="hccbt-container pt-12 md:pt-20 pb-16 md:pb-24 grid lg:grid-cols-12 gap-10 items-center relative">
          <div className="lg:col-span-6">
            <motion.span
              className="kicker"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              data-testid="hero-kicker"
            >
              Ontario · Founding 2025
            </motion.span>

            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl tracking-tight leading-[1.02] mt-5 text-[#0F172A]">
              <motion.span
                className="block"
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 0.05, ease: [0.22, 1, 0.36, 1] }}
              >
                A new chapter in
              </motion.span>
              <motion.span
                className="block italic text-shimmer"
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 0.18, ease: [0.22, 1, 0.36, 1] }}
              >
                career-focused
              </motion.span>
              <motion.span
                className="block"
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 0.32, ease: [0.22, 1, 0.36, 1] }}
              >
                education.
              </motion.span>
            </h1>

            <motion.p
              className="mt-6 text-lg text-[#475569] leading-relaxed max-w-xl"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.45 }}
            >
              Heritage Career College of Business and Technology is a fresh, modern
              college in Ontario — built around mentorship, real industry projects,
              and pathways into Canada's most in-demand careers.
            </motion.p>

            <motion.div
              className="mt-8 flex flex-wrap gap-3"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.55 }}
            >
              <Magnetic>
                <Link to="/programs" className="btn-primary group" data-testid="hero-explore-programs-button">
                  Explore Programs
                  <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
                </Link>
              </Magnetic>
              <Link to="/admissions" className="btn-secondary" data-testid="hero-apply-now-button">
                Apply for 2026 Intake
              </Link>
            </motion.div>

            <Stagger className="mt-12 grid grid-cols-2 sm:grid-cols-4 gap-4" delay={0.7} gap={0.08}>
              {facts.map((f) => (
                <motion.div
                  key={f.label}
                  variants={staggerItem}
                  whileHover={{ y: -4 }}
                  className="card-soft card-lift p-4"
                  data-testid={`fact-${f.label.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  <f.icon size={18} className="text-[#6B9AC4]" />
                  <div className="text-xs text-[#64748B] mt-3 uppercase tracking-wider font-semibold">{f.label}</div>
                  <div className="font-display text-lg mt-1 text-[#0F172A] leading-snug">{f.value}</div>
                </motion.div>
              ))}
            </Stagger>
          </div>

          <motion.div
            className="lg:col-span-6 relative"
            style={{ y: heroY }}
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.9, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
          >
            <div className="absolute -top-6 -right-6 w-40 h-40 rounded-full bg-[#87CEEB]/30 blur-3xl -z-10" />
            <motion.div
              className="rounded-2xl overflow-hidden border border-[#E2E8F0] shadow-[0_24px_60px_-24px_rgba(15,23,42,0.25)] img-hover"
              style={{ scale: heroScale }}
            >
              <img
                src={HERO_IMG}
                alt="Diverse college students together on campus"
                className="w-full h-[380px] md:h-[520px] object-cover"
              />
            </motion.div>
            <motion.div
              className="absolute -bottom-6 -left-6 hidden md:block float-slow"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.8 }}
            >
              <div className="card-soft p-4 max-w-[260px]">
                <div className="overline">Founding Year</div>
                <div className="font-display text-lg mt-2 text-[#0F172A] leading-snug">
                  Now enrolling our founding cohort for the Diploma in Business Management.
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* PILLARS */}
      <section className="bg-white border-b border-[#E2E8F0]">
        <div className="hccbt-container section">
          <Reveal>
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
              <div className="max-w-xl">
                <span className="overline">Why Heritage</span>
                <h2 className="font-display text-3xl sm:text-4xl mt-3 text-[#0F172A] leading-tight">
                  A college built around graduates' careers — not the other way around.
                </h2>
              </div>
              <p className="text-[#475569] max-w-md">
                We think a modern career college should feel personal, rigorous, and
                honest about outcomes. That's the promise we make to every learner.
              </p>
            </div>
          </Reveal>

          <Stagger className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {pillars.map((p) => (
              <motion.div
                key={p.title}
                variants={staggerItem}
                whileHover={{ y: -6 }}
                className="card-soft card-lift p-6"
                data-testid={`pillar-${p.title.toLowerCase().replace(/\s+/g, "-")}`}
              >
                <motion.div
                  className="w-11 h-11 rounded-full bg-[#87CEEB]/20 flex items-center justify-center text-[#0F172A]"
                  whileHover={{ rotate: 8, scale: 1.08 }}
                  transition={{ type: "spring", stiffness: 240, damping: 14 }}
                >
                  <p.icon size={20} />
                </motion.div>
                <h3 className="font-display text-xl mt-5 text-[#0F172A]">{p.title}</h3>
                <p className="text-sm text-[#475569] mt-2 leading-relaxed">{p.body}</p>
              </motion.div>
            ))}
          </Stagger>
        </div>
      </section>

      {/* FEATURED PROGRAM */}
      {activeProgram && (
        <section className="hccbt-container section">
          <div className="grid lg:grid-cols-12 gap-10 items-center">
            <Reveal className="lg:col-span-6">
              <div className="rounded-2xl overflow-hidden border border-[#E2E8F0] img-hover">
                <img
                  src={PROGRAM_IMG}
                  alt="Business management students collaborating"
                  className="w-full h-[380px] object-cover"
                />
              </div>
            </Reveal>
            <Reveal delay={0.1} className="lg:col-span-6">
              <span className="overline">Now Enrolling</span>
              <h2 className="font-display text-3xl sm:text-4xl mt-3 leading-tight text-[#0F172A]">
                {activeProgram.title}
              </h2>
              <p className="mt-5 text-[#475569] leading-relaxed">{activeProgram.summary}</p>
              <div className="mt-6 grid grid-cols-2 gap-4 text-sm">
                <motion.div whileHover={{ y: -3 }} className="card-soft card-lift p-4">
                  <div className="overline">Duration</div>
                  <div className="font-display text-lg mt-1 text-[#0F172A]">{activeProgram.duration}</div>
                </motion.div>
                <motion.div whileHover={{ y: -3 }} className="card-soft card-lift p-4">
                  <div className="overline">Credential</div>
                  <div className="font-display text-lg mt-1 text-[#0F172A]">{activeProgram.credential}</div>
                </motion.div>
              </div>
              <div className="mt-8 flex flex-wrap gap-3">
                <Magnetic>
                  <Link to={`/programs/${activeProgram.slug}`} className="btn-primary group" data-testid="featured-program-cta">
                    View Program
                    <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
                  </Link>
                </Magnetic>
                <Link to="/admissions" className="btn-secondary">How to Apply</Link>
              </div>
            </Reveal>
          </div>
        </section>
      )}

      {/* COMING SOON */}
      <section className="bg-[#0F172A] text-white relative overflow-hidden">
        <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-[#87CEEB]/20 blur-3xl" aria-hidden />
        <div className="hccbt-container section relative">
          <Reveal>
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-10">
              <div>
                <span className="overline" style={{ color: "#87CEEB" }}>Coming Soon</span>
                <h2 className="font-display text-3xl sm:text-4xl mt-3 leading-tight">
                  More programs landing in 2026.
                </h2>
              </div>
              <Link to="/programs" className="text-[#87CEEB] inline-flex items-center gap-2 text-sm font-semibold group" data-testid="home-view-all-programs">
                View all programs <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
              </Link>
            </div>
          </Reveal>

          <Stagger className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {upcoming.map((p) => (
              <motion.div
                key={p.id}
                variants={staggerItem}
                whileHover={{ y: -6, borderColor: "rgba(135,206,235,0.5)" }}
                className="rounded-xl border border-white/10 bg-white/5 p-6 backdrop-blur"
                data-testid={`upcoming-${p.id}`}
              >
                <div className="text-xs text-[#87CEEB] tracking-widest uppercase font-semibold">Coming 2026</div>
                <h3 className="font-display text-xl mt-3 leading-snug">{p.title}</h3>
                <p className="text-sm text-[#CBD5E1] mt-3 leading-relaxed">{p.summary}</p>
              </motion.div>
            ))}
          </Stagger>
        </div>
      </section>

      {/* NEWS PREVIEW removed — no news yet for a founding-year college */}

      {/* CTA BAND */}
      <section className="hccbt-container pb-24">
        <Reveal>
          <motion.div
            whileHover={{ scale: 1.005 }}
            className="rounded-3xl bg-[#87CEEB]/30 border border-[#87CEEB]/40 p-10 md:p-14 grid md:grid-cols-2 gap-8 items-center relative overflow-hidden"
          >
            <div className="absolute -bottom-16 -right-16 w-64 h-64 rounded-full bg-white/40 blur-3xl" aria-hidden />
            <div className="relative">
              <span className="overline">Ready when you are</span>
              <h3 className="font-display text-3xl sm:text-4xl mt-3 text-[#0F172A] leading-tight">
                Build a career you'll be proud of.
              </h3>
              <p className="mt-4 text-[#1e3a5f]/80 max-w-md">
                Speak with admissions about programs, scholarships, and how Heritage
                can fit your goals.
              </p>
            </div>
            <div className="flex flex-wrap gap-3 md:justify-end relative">
              <Magnetic>
                <Link to="/contact" className="btn-primary" data-testid="cta-talk-to-admissions">
                  Talk to Admissions
                </Link>
              </Magnetic>
              <Link to="/programs" className="btn-secondary">Browse Programs</Link>
            </div>
          </motion.div>
        </Reveal>
      </section>
    </div>
  );
};

export default Home;
